import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import SwapVert from '@mui/icons-material/SwapVert';
import {
  CreateLearningPathDialog,
  ReorderPathsDialog,
  StatusMenuButton,
} from './components';
import { isPathsManager } from './utils/is-paths-manager';
import TeaCup from '@/assets/images/svg/tea-cup.svg';
import { LearningTypeList } from '@/components/shared/learning-type-list';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { StateMessage } from '@/components/shared/state-message';
import { useNotification } from '@/context/notification';
import { useManagePathsIndex } from '@/data/learning-path/path-management';
import {
  PathType,
  type LearningPathItem,
} from '@/data/learning-path/interfaces';
import { useBootstrapData } from '@/data/user/bootstrap-data';
import { useUser } from '@/data/user';
import { useSetTitle } from '@/hooks/use-set-title';
import { formatDate } from '@/utils/format-date';

export const PathManagement = () => {
  useSetTitle('Gestão de trilhas');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [reorderScope, setReorderScope] = useState<PathType | null>(null);
  const { globalPaths, companyPaths, isLoading, isFetching, error } =
    useManagePathsIndex();
  const { user } = useUser();
  const { bootstrap } = useBootstrapData();
  const { toast } = useNotification();

  const companyName = bootstrap?.info?.colaborador?.empresa?.empresa;
  const allPaths = [...globalPaths, ...companyPaths];
  const emptyList = !isLoading && (allPaths.length === 0 || error);
  const manager = user && isPathsManager(user);
  const paths = reorderScope === PathType.Company ? companyPaths : globalPaths;

  const mapToListItem = (item: LearningPathItem) => {
    const publishedVersion = item.versions.find((v) => v.published_at);
    const published = Boolean(publishedVersion);
    const formattedDate =
      publishedVersion && formatDate(publishedVersion.published_at!);
    const hasDraft = item.versions.length > 1;
    const shouldShowCustomAction = !item.global || (item.global && manager);

    const handlePathClick = () => {
      toast({
        title: 'Acesso negado',
        content: 'Você não tem permissão para editar trilhas globais.',
        type: 'error',
      });
    };

    const canAccess = !item.global || manager;

    return {
      ...item,
      label: item.title,
      description: published ? `Publicação: ${formattedDate}` : '',
      image: item.header_image_url,
      onClick: !canAccess ? handlePathClick : undefined,
      link: canAccess ? `/empresa/gestao-de-trilhas/${item.id}` : undefined,
      customAction: shouldShowCustomAction ? (
        <StatusMenuButton
          published={published}
          hasDraft={hasDraft}
          pathId={item.id}
          hidden={item.hidden}
        />
      ) : undefined,
    };
  };

  const formattedCompanyPaths = companyPaths?.map(mapToListItem);
  const formattedGlobalPaths = globalPaths?.map(mapToListItem);

  return (
    <PageContainer>
      <PageHeader
        title="Gestão de trilhas"
        description="Crie e gerencie trilhas de aprendizado"
        buttonText="Nova trilha"
        buttonOnClick={() => setShowCreateDialog(true)}
      />
      {emptyList ? (
        <StateMessage
          image={TeaCup}
          title="Você ainda não possui nenhuma trilha..."
          description="Crie uma trilha clicando no botão ’nova trilha’. Suas trilhas aparecerão aqui."
        />
      ) : (
        <Stack gap={4}>
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h2" color="primary.dark">
                Trilhas {companyName || 'da Empresa'}
              </Typography>
              {companyPaths?.length > 1 && (
                <Button
                  startIcon={<SwapVert />}
                  onClick={() => setReorderScope(PathType.Company)}
                  loading={isFetching}
                >
                  Reordenar
                </Button>
              )}
            </Stack>
            <LearningTypeList
              data={formattedCompanyPaths}
              isLoading={isLoading}
            />
          </Box>
          {manager && (
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h2" color="primary.dark">
                  Roteiros de Aprendizagem Galena
                </Typography>
                {globalPaths?.length > 1 && (
                  <Button
                    startIcon={<SwapVert />}
                    onClick={() => setReorderScope(PathType.Global)}
                    loading={isFetching}
                  >
                    Reordenar
                  </Button>
                )}
              </Stack>
              <LearningTypeList
                data={formattedGlobalPaths}
                isLoading={isLoading}
              />
            </Box>
          )}
        </Stack>
      )}
      {showCreateDialog && (
        <CreateLearningPathDialog onClose={() => setShowCreateDialog(false)} />
      )}
      {reorderScope && (
        <ReorderPathsDialog
          paths={paths}
          scope={reorderScope}
          onClose={() => setReorderScope(null)}
        />
      )}
    </PageContainer>
  );
};
